/**
 * /eye-contact — исправление взгляда в камеру на видео.
 *
 * Ход работы:
 *   1. Файл → первый кадр на экране сразу, потом РАЗБОР: проход конвейера
 *      WebCodecs `geometry2d.analyzeOnly` по всем кадрам (MediaPipe находит
 *      веки, радужку, позу головы; ничего не кодируется). Для контейнеров,
 *      которых mp4box не читает (webm/mkv), — перемоткой <video> на середину
 *      кадра (k + 0.5)/fps.
 *   2. План (gaze-plan.js) — чистая математика над собранными точками,
 *      пересчитывается на каждое движение ползунка за миллисекунды.
 *   3. Превью: кадр <video> на холст + перерисовка глаз по плану ЭТОГО кадра.
 *      «Оригинал» и удержание кадра — сравнение с исходником в любой момент.
 *   4. Сохранение: второй проход того же конвейера рисует каждый кадр по
 *      плану, звук переносится как есть (HW-лейн) или доклеивается (перемотка).
 *
 * Разбор — это не «ожидание перед работой», а сама работа: без будущего
 * кадров не бывает ни симметричного сглаживания, ни плавного включения до
 * моргания, ни «куда человек смотрит большую часть ролика».
 */
import { EYE_FLOATS, packEyes, eyeFrom, headAngles, smoothEyes } from './eye-geom.js';
import { measure, plan, gaussSmooth, GATE } from './gaze-plan.js';
import { warpEye, eyeROI } from './eye-warp.js';

const CFG = window.__EC || {};
const I = CFG.i18n || {};
const SLUG = 'eye-contact';
const STRIDE = EYE_FLOATS * 2;
const ANALYZE_MAX_SIDE = 1920;     // разбор: длинная сторона холста кадра
const DETECT_MAX = 640;            // вход детектора: лицо ≥ 256 px в модели
const MAX_SEC = 20 * 60;           // дольше — честный отказ до работы
const SEEK_DEADLINE_MS = 2500;
const LOAD_DEADLINE_MS = 20000;    // файл обязан показать кадр за это ВИДИМОЕ время
const STALL_MS = 60000;            // разбор/сохранение без единого кадра столько — отказ
// После ПОСЛЕДНЕГО кадра конвейер ещё собирает звук (не-AAC дорожка
// перекодируется целиком) — это легитимная тишина, и окно растёт с длиной:
// сторож, убивающий здоровое сохранение 20-минутной записи, хуже его отсутствия.
const STALL_FINAL_MS = 180000;
// «Смотрит в объектив» — взгляд не дальше 5° от камеры: столько зритель
// считывает как зрительный контакт.
const CONTACT_DEG = 5;
// Палец на кадре: оригинал — ДОЛГОЕ нажатие; движение дальше порога — прокрутка.
const HOLD_DELAY_MS = 180;
const HOLD_SLOP_PX = 10;
// «Крупно глаза»: во всю ширину сцены — 2.1 расстояния между глазами (оба
// глаза с запасом по краям).
const ZOOM_SPAN = 2.1;
// Заплатка глаза в превью: на ходу — не шире стольких пикселей на глаз (успеть
// за плеером), на паузе — родной размер ролика, но не больше потолка.
const PLAY_EYE_PX = 140;
const PAUSE_EYE_PX = 440;
function stallMs() {
    // Ручка стенда (tests/playwright/eye-contact-a-stalled-lane-falls-back.mjs):
    // ждать минуту в каждом прогоне сторожа нельзя.
    const k = +window.__ecStallMs;
    return k > 0 ? k : STALL_MS;
}

const $ = (id) => document.getElementById(id);
const fmt = (s, o) => String(s || '').replace(/:(\w+)/g, (m, k) => (o && o[k] !== undefined && o[k] !== null ? o[k] : m));
const clamp = (x, a, b) => (x < a ? a : x > b ? b : x);
function ae(f, d) { try { window.__ae && window.__ae('feature_use', Object.assign({ f }, d || {})); } catch (_) { /* телеметрия не валит тул */ } }
function aeRaw(ev, d) { try { window.__ae && window.__ae(ev, d || {}); } catch (_) { /* то же */ } }
function stage(n, d) { try { window.__stage && window.__stage(n, d); } catch (_) { /* то же */ } }
function toast(msg, type) { try { window.__toast && window.__toast(msg, type || 'info'); } catch (_) { /* то же */ } }
// Сроки медиа — по ВИДИМОМУ времени: скрытая вкладка вправе не декодировать
// (CLAUDE.md, DeferMediaLoad), и «человек отвлёкся» не должно становиться
// «файл сломан».
function visNow() { try { return window.__visibleNow ? window.__visibleNow() : performance.now(); } catch (_) { return performance.now(); } }
function visTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    const id = setTimeout(fn, ms);
    return () => clearTimeout(id);
}
/**
 * Шаг с часами тишины: work(signal, beat, alive) зовёт beat() на каждом кадре.
 * Разбор и сохранение обязаны либо ДВИГАТЬСЯ, либо ОТКАЗАТЬ: вуаль, под
 * которой минуту не пришло ни кадра, — это «сломано» без слова (ловилось
 * стендом на загруженной машине: сохранение стояло 10+ минут). Застрявший
 * шаг отцепляется (alive() → false: его поздние кадры больше не трогают
 * детектор), а свой AbortController — дочерний, чтобы запасной лейн после
 * тишины не стартовал уже отменённым. limit() — допустимая тишина СЕЙЧАС
 * (у сохранения после последнего кадра она длиннее, см. STALL_FINAL_MS).
 */
async function guarded(work, what, limit) {
    const child = new AbortController();
    const parent = S.abort;
    const onParent = () => child.abort();
    if (parent) { if (parent.signal.aborted) child.abort(); else parent.signal.addEventListener('abort', onParent, { once: true }); }
    let last = visNow(), timer = 0, alive = true;
    const beat = () => { last = visNow(); };
    const stall = new Promise((_, rej) => {
        timer = setInterval(() => {
            if (visNow() - last <= (limit ? limit() : stallMs())) return;
            clearInterval(timer);
            rej(Object.assign(new Error(what + '-stalled'), { ecStall: true }));
        }, 2000);
    });
    const p = work(child.signal, beat, () => alive);
    p.catch(() => { /* проигравшая сторона гонки — не необработанный отказ */ });
    try { return await Promise.race([p, stall]); }
    finally {
        alive = false;
        clearInterval(timer);
        if (parent) parent.signal.removeEventListener('abort', onParent);
        child.abort();
    }
}
/**
 * Браузер без VideoEncoder не соберёт видео НИКАКИМ лейном (оба кодируют
 * через WebCodecs). Раньше человек узнавал это только на «Сохранить» —
 * после разбора, да ещё с советом «перекодируйте в MP4», который не
 * помогает (эмуляция старого iPhone, 25.09). Теперь — до работы.
 */
function noEncoder() { return typeof window.VideoEncoder === 'undefined'; }
/* The user's Cancel is THIS operation's own controller — not a message with
   «abort» in it: MediaPipe dies with «Aborted()», and read as a cancel the
   detector's death said «cancelled» instead of showFail('detector'). The
   controller is captured by the caller: S.abort is cleared before the check. */
function isAbort(e, ctrl) {
    const sig = ctrl && ctrl.signal;
    if (window.__isUserCancel) return window.__isUserCancel(e, sig);
    return !!(sig ? sig.aborted : (e && e.name === 'AbortError'));
}
function human(sec) {
    sec = Math.max(0, Math.round(sec));
    if (sec < 60) return sec + ' ' + (I.sec_unit || 's');
    return Math.round(sec / 60) + ' ' + (I.min_unit || 'min');
}
function clock(t) {
    t = Math.max(0, t || 0);
    const m = Math.floor(t / 60), s = Math.floor(t % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
}

const S = {
    file: null, limitKey: null, url: '', src: null, previewLost: '', repointing: false,
    W: 0, H: 0, fps: 30, dur: 0, wcCap: null, lane: 'hw',
    an: null, M: null, P: null, zoomC: null,
    analyzing: false, exporting: false, abort: null,
    params: { strength: 1, keep: 0.75, horizontal: true, aimV: 0, aimH: 0, lookAway: 24, anchor: -1 },
    view: 'fixed', holding: false, zoom: false, playing: false,
    lastIdx: -1, rvfc: 0, raf: 0, shownT: -1, patch: null,
};
const el = {};

/* ───────────────────────────── настройки ───────────────────────────────── */
const PREF = ['strength', 'keep', 'horizontal', 'lookAway'];
function loadPrefs() {
    const ts = window.__toolSettings;
    if (!ts || !ts.load) return;
    for (const k of PREF) {
        const v = ts.load(SLUG, k, null);
        if (v === null || v === undefined || v === '') continue;
        if (k === 'horizontal') S.params.horizontal = v === true || v === 'true' || v === 1 || v === '1';
        else if (isFinite(+v)) S.params[k] = +v;
    }
}
function savePref(k, v) { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) { /* хранилище недоступно — не страшно */ } }

/* ───────────────────────────── отказ и вуаль ───────────────────────────── */
function showFail(kind) {
    const box = el.fail;
    if (!box) return;
    if (!kind) { box.hidden = true; box.textContent = ''; return; }
    box.textContent = '';
    const p = document.createElement('span');
    p.textContent = kind === 'no_face' ? [I.no_face, I.no_face_hint].filter(Boolean).join(' ') : (I['fail_' + kind] || '');
    box.appendChild(p);
    if (kind === 'open' && CFG.converterUrl) {
        box.appendChild(document.createTextNode(' '));
        const a = document.createElement('a');
        a.href = CFG.converterUrl; a.textContent = I.fail_convert || 'Converter';
        box.appendChild(a);
    }
    box.hidden = false;
}
function veil(on, txt, frac, sub) {
    if (!el.veil) return;
    el.veil.hidden = !on;
    if (!on) return;
    el.veilTxt.textContent = txt || '';
    el.veilFill.style.width = Math.round(clamp(frac || 0, 0, 1) * 100) + '%';
    el.veilSub.textContent = sub || '';
    el.veilStop.hidden = !S.abort;
}
function progress(on, txt, frac, sub) {
    if (!el.prog) return;
    el.prog.hidden = !on;
    if (!on) return;
    el.progTxt.textContent = txt || '';
    el.progFill.style.width = Math.round(clamp(frac || 0, 0, 1) * 100) + '%';
    el.progEta.textContent = sub || '';
    el.cancel.hidden = !S.abort;
}
function lock(on) {
    for (const n of el.rail.querySelectorAll('input,button')) n.disabled = !!on;
    el.exportBtn.disabled = !!on || !S.P;
    el.exportBtn.setAttribute('aria-disabled', el.exportBtn.disabled ? 'true' : 'false');
}

/* ───────────────────────────── файл ────────────────────────────────────── */
/* Превью <video> читает ПРОЧНУЮ копию, которую аплоадер уже сделал (RAM или
   OPFS — FileBytes.settle, CLAUDE.md), а не хэндл системного выбора файла.
   Android отзывает этот хэндл, пока страница работает: на ролике 86 МБ и
   разборе 16 минут (444 с вкладка была скрыта) конвейер разбора спасался
   копией сам (hw_pipeline_slice_rescued_from_tier), а <video> получил
   сетевую ошибку — Chromium считает такие медиаданные чужими
   (IsMediaDataCorsSameOrigin → false), холст превью «заражается», и каждый
   getImageData в correctFrame бросал SecurityError: Play, вид, перемотка,
   сохранение — шесть раз за минуту, результата нет (Sentry
   TIMBRICA-FRONTEND-NK, Chrome 154, Android 10). settle() сам решает, ждать
   ли недописанную копию (телефон — ждёт), и никогда не читает файл целиком. */
async function durableSource(file) {
    const FB = window.FileBytes;
    if (!FB || typeof FB.settle !== 'function') return file;
    try { return (await FB.settle(file)) || file; } catch (_) { return file; }
}
/**
 * Ролик «открылся» — это когда известны его размер и длина (loadedmetadata),
 * а НЕ когда декодирован первый кадр (loadeddata). iPhone первый кадр без
 * воспроизведения или перемотки не декодирует вовсе: событие loadeddata там
 * не приходило, через 20 с страница говорила «не удалось открыть файл» — так
 * кончались 32 выбора файла из 40 на iOS (телеметрия 25.09–13.10.2026;
 * на Android — ни одного). Кадр даёт следующая же перемотка (seekTo).
 */
function waitLoaded(v, ms) {
    return new Promise((res, rej) => {
        let cancel = null;
        const done = (fn) => () => {
            if (cancel) cancel();
            v.removeEventListener('loadedmetadata', ok);
            v.removeEventListener('error', bad);
            fn();
        };
        const ok = done(res);
        const bad = done(() => rej(new Error('decode')));
        if (v.readyState >= 1 && !v.error) { res(); return; }
        v.addEventListener('loadedmetadata', ok);
        v.addEventListener('error', bad);
        cancel = visTimeout(done(() => rej(new Error('load-timeout'))), ms);
    });
}
const isTaint = (e) => !!(e && e.name === 'SecurityError');
/* Кадры превью больше не читаются (файл пропал из-под <video>): сказать это
   ОДИН раз и перестать исправлять, а не бросать на каждом кадре. */
function previewLost(why) {
    if (S.previewLost) return;
    S.previewLost = why || 'lost';
    stopPlayback();
    showFail('lost');
    aeRaw('tool_error', { f: 'preview_lost', why: S.previewLost, lane: S.lane, mb: Math.round(((S.file && S.file.size) || 0) / 1048576) });
}
/* <video> упал ПОСЛЕ загрузки. Если копия аплоадера к этому времени готова
   (на компьютере settle её не ждёт), превью переезжает на неё и встаёт туда,
   где было; копии нет — честный отказ. */
async function onPreviewMediaError(file) {
    if (S.file !== file || S.previewLost || S.repointing) return;
    S.repointing = true;
    const v = el.video, at = v.currentTime || 0;
    try {
        const src = await durableSource(file);
        if (S.file !== file) return;
        if (src && src !== S.src) {
            const old = S.url;
            S.src = src;
            S.url = URL.createObjectURL(src);
            v.src = S.url;
            watchFrames();
            try { URL.revokeObjectURL(old); } catch (_) { /* не наш */ }
            await waitLoaded(v, LOAD_DEADLINE_MS);
            if (S.file !== file) return;
            await seekTo(at, 1500);
            aeRaw('tool_diagnostic', { issue: 'ec_preview_repointed' });
            drawFrame();
            return;
        }
    } catch (_) {
        /* копия тоже не открылась — ниже честный отказ */
    } finally {
        S.repointing = false;
    }
    if (S.file === file) previewLost('media_error');
}
async function load(file) {
    stopPlayback();
    if (S.abort) { try { S.abort.abort(); } catch (_) { /* уже отменён */ } }
    showFail(false);
    S.file = file; S.an = null; S.M = null; S.P = null; S.params.anchor = -1; S.params.aimV = 0; S.params.aimH = 0;
    S.lastIdx = -1; S.previewLost = '';
    try { S.limitKey = window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null; } catch (_) { S.limitKey = null; }
    const v = el.video;
    v.onerror = null;
    const src = await durableSource(file);
    if (S.file !== file) return;   // пока ждали копию, выбрали другой файл
    if (S.url) { try { URL.revokeObjectURL(S.url); } catch (_) { /* не наш */ } }
    S.src = src;
    S.url = URL.createObjectURL(src);
    v.muted = true;
    v.src = S.url;
    watchFrames();
    try {
        await waitLoaded(v, LOAD_DEADLINE_MS);
    } catch (e) {
        if (S.file !== file) return;   // пока ждали, выбрали другой файл
        showFail('open'); aeRaw('tool_error', { f: e && e.message === 'load-timeout' ? 'load_timeout' : 'decode_failed' });
        return;
    }
    if (S.file !== file) return;
    // Загружено — теперь ошибка элемента значит «файл пропал из-под превью».
    v.onerror = () => { onPreviewMediaError(file); };
    // <video> на MPEG-4 Part 2 / ProRes «открывается» с размером 0×0 БЕЗ ошибки —
    // проверять по размерам, иначе прибор открывается пустым (CLAUDE.md).
    S.W = v.videoWidth; S.H = v.videoHeight; S.dur = v.duration || 0;
    if (!S.W || !S.H || !isFinite(S.dur) || S.dur <= 0) {
        showFail('open'); aeRaw('tool_error', { f: 'no_dims' });
        return;
    }
    stage('file_picked', { mb: Math.round((file.size || 0) / 1048576), sec: Math.round(S.dur), w: S.W, h: S.H });
    if (S.dur > MAX_SEC) {
        showFail('too_long');
        ae('ec_too_long', { sec: Math.round(S.dur) });
        return;
    }
    if (noEncoder()) {
        showFail('no_encoder');
        aeRaw('tool_blocked', { f: 'no_export_support', why: 'no-webcodecs', at: 'file' });
        return;
    }
    el.work.hidden = false;
    el.timeDur.textContent = clock(S.dur);
    sizeCanvas();
    await seekTo(Math.min(0.1, S.dur / 2));
    drawFrame();
    S.fps = await probeFps();
    S.lane = pickLane(file);
    await analyze();
}

function isMp4Family(file) {
    const n = (file.name || '').toLowerCase(), t = (file.type || '').toLowerCase();
    return /\.(mp4|m4v|mov|3gp)$/.test(n) || /video\/(mp4|quicktime|3gpp)/.test(t);
}
function pickLane(file) {
    const force = window.__ecLane;                // отладка и стенды: 'hw' | 'seek'
    if (force === 'hw' || force === 'seek') return force;
    const P = window.WebCodecsPipeline;
    if (!P || typeof P.run !== 'function') return 'seek';
    if (!S.wcCap || !S.wcCap.ok) return 'seek';
    return isMp4Family(file) ? 'hw' : 'seek';
}
async function probeFps() {
    // Частоту спрашивать у КОНТЕЙНЕРА: по показанным кадрам браузер
    // выбрасывает дубликаты (память /video-retouch: 25 к/с «мерились» как 12).
    try {
        if (window.WebCodecsFrameExtractor && S.file) {
            let cancel = null;
            const cap = await Promise.race([
                window.WebCodecsFrameExtractor.isSupported(S.file),
                // Срок — против ВЕЧНОГО ожидания, а не медленного: под нагрузкой проба
                // идёт 10+ с, а ответ «не знаю» отправляет в лейн перемотки, который
                // в разы медленнее и доклеивает звук отдельно (стенд 24.09).
                new Promise((res) => { cancel = visTimeout(() => res(null), 30000); }),
            ]);
            if (cancel) cancel();
            if (!cap) aeRaw('tool_diagnostic', { issue: 'ec_fps_probe_timeout' });
            S.wcCap = cap;
            if (cap && typeof cap.fps === 'number' && cap.fps > 1 && cap.fps <= 240) return Math.round(cap.fps * 1000) / 1000;
        }
    } catch (_) { /* не разобрали — ниже запасной ответ */ }
    return 30;
}
/**
 * Перемотать и дождаться, пока кадр ПОКАЗАН: `seeked` сам по себе этого не
 * обещает (кадр ещё идёт к композитору), и drawImage снимает прежний —
 * общий примитив __seekPresented (CLAUDE.md). Разбор и сохранение перемоткой
 * на таком кадре получали бы точки соседнего кадра.
 */
function seekTo(t, deadline) {
    const v = el.video;
    const target = clamp(t, 0, Math.max(0, S.dur - 0.02));
    if (typeof window.__seekPresented === 'function') {
        return window.__seekPresented(v, target, deadline || SEEK_DEADLINE_MS).then((via) => via !== 'timeout' && via !== 'seek_error');
    }
    return new Promise((res) => {
        let done = false, cancel = null;
        const ok = (missed) => { if (done) return; done = true; if (cancel) cancel(); res(!missed); };
        if (Math.abs(v.currentTime - target) < 1e-4 && v.readyState >= 2) { ok(false); return; }
        v.onseeked = () => ok(false);
        cancel = visTimeout(() => ok(true), deadline || SEEK_DEADLINE_MS);
        try { v.currentTime = target; } catch (_) { ok(true); }
    });
}
/**
 * Какой кадр плеер ПОКАЗЫВАЕТ — время кадра от самого плеера
 * (requestVideoFrameCallback), а не currentTime: часы плеера стоят где-то
 * внутри кадра, а после перемотки — и вовсе на границе, и «ближайший кадр
 * разбора» в 10 случаях из 16 оказывался СЛЕДУЮЩИМ (замер 14.10.2026 на
 * ролике с номером кадра в картинке): глаза перерисовывались по точкам не
 * того кадра. Сторож — tests/playwright/eye-contact-preview-frame-sync.mjs.
 */
function watchFrames() {
    const v = el.video;
    if (S.rvfc && v.cancelVideoFrameCallback) { try { v.cancelVideoFrameCallback(S.rvfc); } catch (_) { /* уже снят */ } }
    S.rvfc = 0; S.shownT = -1;
    if (typeof v.requestVideoFrameCallback !== 'function') return;
    const tick = (now, md) => {
        S.shownT = md && typeof md.mediaTime === 'number' ? md.mediaTime : -1;
        S.rvfc = v.requestVideoFrameCallback(tick);
        if (!v.paused && S.playing) drawFrame();
    };
    S.rvfc = v.requestVideoFrameCallback(tick);
}

/* ───────────────────────────── разбор ──────────────────────────────────── */
function newAnalysis(n) {
    return {
        n, W: S.W, H: S.H, fps: S.fps,
        t: new Float32Array(n), has: new Uint8Array(n), eyes: new Float32Array(n * STRIDE),
        pitch: new Float32Array(n), yaw: new Float32Array(n), blink: new Float32Array(n),
        box: new Float32Array(n * 4),
    };
}
function growAnalysis(an, n) {
    const out = newAnalysis(n);
    out.t.set(an.t.subarray(0, Math.min(an.n, n)));
    out.has.set(an.has.subarray(0, Math.min(an.n, n)));
    out.eyes.set(an.eyes.subarray(0, Math.min(an.n, n) * STRIDE));
    out.pitch.set(an.pitch.subarray(0, Math.min(an.n, n)));
    out.yaw.set(an.yaw.subarray(0, Math.min(an.n, n)));
    out.blink.set(an.blink.subarray(0, Math.min(an.n, n)));
    out.box.set(an.box.subarray(0, Math.min(an.n, n) * 4));
    return out;
}
function iou(a, b) {
    const x1 = Math.max(a.x, b.x), y1 = Math.max(a.y, b.y);
    const x2 = Math.min(a.x + a.w, b.x + b.w), y2 = Math.min(a.y + a.h, b.y + b.h);
    if (x2 <= x1 || y2 <= y1) return 0;
    const inter = (x2 - x1) * (y2 - y1);
    return inter / (a.w * a.h + b.w * b.h - inter);
}
/** Главное лицо: продолжение прошлого (по пересечению рамок), иначе самое крупное. */
function pickMain(faces, prev) {
    if (!faces || !faces.length) return null;
    if (prev) {
        let best = null, bi = 0.25;
        for (const f of faces) { const v = iou(f.box, prev); if (v > bi) { bi = v; best = f; } }
        if (best) return best;
    }
    return faces.reduce((a, b) => (b.box.w * b.box.h > a.box.w * a.box.h ? b : a));
}
function record(an, i, t, face) {
    an.t[i] = t;
    if (!face || !face.pts || face.pts.length < 478) return;
    packEyes(face.pts, S.W, S.H, an.eyes, i * STRIDE);
    const ang = headAngles(face.m);
    an.pitch[i] = isFinite(ang.pitch) ? ang.pitch : 0;
    an.yaw[i] = isFinite(ang.yaw) ? ang.yaw : 0;
    const bs = face.bs || {};
    an.blink[i] = Math.max(bs.eyeBlinkLeft || 0, bs.eyeBlinkRight || 0);
    an.box[i * 4] = face.box.x; an.box[i * 4 + 1] = face.box.y; an.box[i * 4 + 2] = face.box.w; an.box[i * 4 + 3] = face.box.h;
    an.has[i] = 1;
}

async function ensureDetector() {
    const FR = window.FRLandmarks;
    if (!FR) throw Object.assign(new Error('detector-script-missing'), { ecKind: 'detector' });
    veil(true, I.loading_model || '', 0.02, '');
    // Ход скачивания — на вуали: на медленной сети модель идёт минуту и
    // больше, и немая строка «Загружаю…» всё это время выглядит зависанием
    // (стенд под нагрузкой: 100 с без единого признака жизни).
    const off = FR.onProgress ? FR.onProgress((p) => {
        if (!S.analyzing || !p || p.phase !== 'model' || !(p.total > 0)) return;
        const f = clamp(p.loaded / p.total, 0, 1);
        veil(true, I.loading_model || '', 0.02 + 0.9 * f, fmt(I.pct, { n: Math.round(f * 100) }));
    }) : null;
    try {
        await FR.ensure(CFG.sources || [], { numFaces: 2, mode: 'IMAGE', blendshapes: true, matrix: true });
    } catch (e) {
        if (FR.isUnavailable && FR.isUnavailable(e)) e.ecKind = 'detector';
        throw e;
    } finally {
        if (off) off();
    }
}

async function analyze() {
    S.analyzing = true;
    S.abort = new AbortController();
    const ctrl = S.abort;
    lock(true);
    const t0 = performance.now();
    let an = null, retried = false;
    try {
        await ensureDetector();
        // Модель готова — вуаль сразу говорит о РАЗБОРЕ: до первого кадра
        // конвейер может думать секунды, и «модель 100 %» читалось бы зависанием.
        progressAnalyze(0, Math.max(1, Math.round(S.dur * S.fps)), t0);
        for (;;) {
            try {
                an = S.lane === 'hw'
                    ? await guarded((sig, beat, alive) => analyzeHw(t0, sig, beat, alive), 'analyze')
                    : await guarded((sig, beat, alive) => analyzeSeek(t0, sig, beat, alive), 'analyze');
                break;
            } catch (e) {
                if (isAbort(e, ctrl)) throw e;
                const FR = window.FRLandmarks;
                // Смерть wasm посреди прохода — лечится следующим кандидатом и ОДНИМ повтором.
                if (!retried && FR && FR.isWasmDeath && FR.isWasmDeath(e.inner || e)) {
                    retried = true;
                    await FR.recover(e.inner || e);
                    continue;
                }
                // Конвейер отказал (кодек, контейнер) — тот же разбор перемоткой.
                if (S.lane === 'hw' && !e.ecKind) {
                    aeRaw('tool_diagnostic', { issue: 'ec_hw_analyze_failed', msg: String(e && e.message).slice(0, 140) });
                    S.lane = 'seek';
                    continue;
                }
                throw e;
            }
        }
    } catch (e) {
        S.analyzing = false; S.abort = null; veil(false); lock(false);
        if (isAbort(e, ctrl)) { toast(I.cancelled || '', 'info'); return; }
        // Разбор перемоткой читает кадры с <video>: файл, пропавший из-под него,
        // — не «нечитаемый ролик», а потерянный доступ.
        if (isTaint((e && e.inner) || e)) { previewLost('tainted'); return; }
        showFail(e && e.ecKind === 'detector' ? 'detector' : 'analyze');
        aeRaw('tool_error', { f: e && e.ecStall ? 'analyze_stall' : 'analyze_failed', lane: S.lane, msg: String(e && e.message).slice(0, 140) });
        return;
    }
    S.analyzing = false; S.abort = null;
    smoothEyes(an);
    S.an = an;
    S.M = measure(an);
    buildZoomTrack();
    const faced = an.has.reduce((s, x) => s + x, 0);
    veil(false);
    lock(false);
    if (faced < Math.max(3, an.n * 0.05)) {
        showFail('no_face');
        aeRaw('tool_funnel', { f: 'no_face', frames: an.n, faced });
        return;
    }
    replan();
    stage('analyzed', {
        frames: an.n, faced, lane: S.lane, ms: Math.round(performance.now() - t0),
        msPerFrame: an.n ? Math.round((performance.now() - t0) / an.n) : 0,
    });
    drawFrame();
}

async function analyzeHw(t0, sig, beat, alive) {
    const P = window.WebCodecsPipeline, FRV = window.FRVideo;
    const W = S.W, H = S.H;
    const k = Math.min(1, ANALYZE_MAX_SIDE / Math.max(W, H));
    const aw = Math.max(2, Math.round(W * k)), ah = Math.max(2, Math.round(H * k));
    const finder = FRV.createFinder({ track: true, maxFaces: 1, deep: 'lost', detectMax: DETECT_MAX });
    let an = newAnalysis(Math.max(8, Math.ceil(S.dur * S.fps) + 4));
    let n = 0, prev = null, innerErr = null;
    const expect = Math.max(1, Math.round(S.dur * S.fps));
    await P.run({
        slug: SLUG, file: S.file, params: {}, crf: 23,
        geometry2d: {
            // Кодер требует чётных сторон даже там, где ничего не кодирует.
            width: aw - (aw & 1), height: ah - (ah & 1), analyzeOnly: true,
            draw(gctx, drawVideo, cw, ch, idx, t) {
                if (innerErr || !alive()) return;
                beat();
                drawVideo(0, 0, W, H, 0, 0, cw, ch);
                if (n >= an.n) an = growAnalysis(an, an.n * 2);
                let face = null;
                try { face = pickMain(finder.find(gctx.canvas, cw, ch), prev); }
                catch (e) { innerErr = e; return; }
                record(an, n, t, face);
                prev = face ? face.box : prev;
                n++;
                if (n % 15 === 1) progressAnalyze(n, expect, t0);
            },
        },
        onProgress() { /* ход считаем сами по кадрам */ },
        abortSignal: sig,
    });
    if (innerErr) throw Object.assign(new Error('detect-failed'), { inner: innerErr });
    if (!n) throw new Error('hw-no-frames');
    an = growAnalysis(an, n); an.n = n;
    return an;
}

async function analyzeSeek(t0, sig, beat, alive) {
    const FRV = window.FRVideo;
    const W = S.W, H = S.H;
    const k = Math.min(1, ANALYZE_MAX_SIDE / Math.max(W, H));
    const cv = document.createElement('canvas');
    cv.width = Math.max(2, Math.round(W * k)); cv.height = Math.max(2, Math.round(H * k));
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    const finder = FRV.createFinder({ track: true, maxFaces: 1, deep: 'lost', detectMax: DETECT_MAX });
    const n = Math.max(1, Math.floor(S.dur * S.fps));
    const an = newAnalysis(n);
    let prev = null;
    for (let i = 0; i < n; i++) {
        if (sig.aborted || !alive()) throw new DOMException('cancelled', 'AbortError');
        beat();
        // Середина кадра: на границе браузер отдаёт ПРЕДЫДУЩИЙ кадр (память /video-retouch).
        await seekTo((i + 0.5) / S.fps);
        ctx.drawImage(el.video, 0, 0, cv.width, cv.height);
        let face = null;
        try { face = pickMain(finder.find(cv, cv.width, cv.height), prev); }
        catch (e) { throw Object.assign(new Error('detect-failed'), { inner: e }); }
        // Время кадра — его НАЧАЛО, как у кадров конвейера: по нему плеер и
        // сохранение находят кадр («последний, чьё время не позже»).
        record(an, i, i / S.fps, face);
        prev = face ? face.box : prev;
        if (i % 5 === 0) progressAnalyze(i + 1, n, t0);
    }
    return an;
}

function progressAnalyze(done, total, t0) {
    const f = clamp(done / total, 0, 1);
    const el_ = (performance.now() - t0) / 1000;
    const eta = f > 0.03 ? Math.max(0, el_ / f - el_) : 0;
    veil(true, fmt(I.analyzing, { done, total }), f, eta ? fmt(I.eta, { t: human(eta) }) : '');
}

/* ───────────────────────────── план ────────────────────────────────────── */
function replan() {
    if (!S.an) return;
    S.P = plan(S.an, S.M, S.params);
    paintStats();
    paintGraph();
    lock(false);
}
/** Доля кадров с открытыми глазами, где взгляд в объективе, — до и после. */
function contactShares() {
    const P = S.P, an = S.an;
    if (!P || !an) return null;
    let n = 0, b = 0, a = 0;
    for (let i = 0; i < an.n; i++) {
        if (!an.has[i]) continue;
        const g = P.gate[i];
        if (g === GATE.CLOSED || g === GATE.NO_FACE || g === GATE.SMALL) continue;
        n++;
        if (Math.hypot(P.gv[i], P.gh[i]) <= CONTACT_DEG) b++;
        if (Math.hypot(P.gvAfter[i], P.ghAfter[i]) <= CONTACT_DEG) a++;
    }
    return n ? { before: b / n, after: a / n } : null;
}
function paintStats() {
    const st = S.P && S.P.stats;
    if (!st || !el.stats) return;
    const lines = [];
    const pct = (x) => Math.round(x * 100);
    // Первая строка — то, ради чего пришли: сколько времени человек смотрит
    // в объектив до и после. Проценты кадров исправлено — техника, она ниже.
    const c = contactShares();
    if (c) lines.push(fmt(I.stats_contact, { before: pct(c.before), after: pct(c.after) }));
    if (st.correctedShare < 0.02 || st.meanCorrectionDeg < 1.5) lines.push(I.stats_ok || '');
    else {
        lines.push(fmt(I.stats_fixed, { pct: pct(st.correctedShare) }));
        const mv = st.medianGazeV;
        if (Math.abs(mv) >= 2) lines.push(fmt(mv > 0 ? I.stats_shift : I.stats_shift_up, { deg: Math.round(Math.abs(mv)) }));
    }
    if (st.closedShare >= 0.05) lines.push(fmt(I.stats_closed, { pct: pct(st.closedShare) }));
    if (st.lookAwayShare >= 0.05) lines.push(fmt(I.stats_away, { pct: pct(st.lookAwayShare) }));
    const lim = S.P.limited.reduce((s, x) => s + x, 0) / Math.max(1, st.faceFrames);
    if (lim >= 0.05) lines.push(fmt(I.stats_limited, { pct: pct(lim) }));
    el.stats.textContent = '';
    lines.filter(Boolean).forEach((t, i) => {
        const p = document.createElement(i ? 'span' : 'strong');
        p.textContent = t;
        el.stats.appendChild(p);
    });
    // Смотрел заметно ниже камеры — значит, текст стоял далеко от объектива.
    // Совет на СЛЕДУЮЩУЮ запись: исправлять нечего будет вовсе.
    if (st.medianGazeV >= 8 && CFG.teleprompterUrl && I.tip_next) {
        const tip = document.createElement('span');
        tip.className = 'ec-tip';
        tip.appendChild(document.createTextNode(I.tip_next + ' '));
        const a = document.createElement('a');
        a.href = CFG.teleprompterUrl; a.target = '_blank'; a.rel = 'noopener';
        a.textContent = I.tip_link || '';
        a.addEventListener('click', () => ae('ec_tip_click'));
        tip.appendChild(a);
        el.stats.appendChild(tip);
    }
}

/* ───────────────────────────── превью ──────────────────────────────────── */
function sizeCanvas() {
    const box = el.view.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const bw = Math.max(200, box.width || 640);
    // Высота кадра ограничена экраном: прибор целиком в окне (1280×800).
    const maxH = Math.max(200, Math.min(window.innerHeight * 0.55, 620));
    let w = bw, h = bw * S.H / S.W;
    if (h > maxH) { h = maxH; w = h * S.W / S.H; }
    let cw = Math.round(Math.min(S.W, w * dpr)), ch = Math.round(cw * S.H / S.W);
    if (S.zoom) {
        // «Крупно глаза» занимает ВСЮ сцену — ту же высоту, что и кадр, но во
        // всю её ширину, а не кадр в его пропорциях: у вертикального ролика
        // кадр на сцене — узкий столбик, и «крупно» в нём было те же 40 px на
        // глаз (обращение #1901). Высота та же — страница под сценой не прыгает.
        w = bw;
        cw = Math.round(w * dpr); ch = Math.round(h * dpr);
    }
    el.canvas.style.width = Math.round(w) + 'px';
    el.canvas.style.height = Math.round(h) + 'px';
    if (el.canvas.width !== cw || el.canvas.height !== ch) { el.canvas.width = cw; el.canvas.height = ch; }
}
/**
 * Кадр разбора, который показан в момент t: ПОСЛЕДНИЙ, чьё время не позже t
 * (четверть кадра — на округление меток). Не «ближайший»: во второй половине
 * своего интервала кадр ближе к следующему.
 */
function frameIndex(t) {
    const an = S.an;
    if (!an || !an.n) return -1;
    const T = an.t, x = t + 0.25 / (S.fps || 30);
    let lo = 0, hi = an.n - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (T[mid] <= x) lo = mid; else hi = mid - 1; }
    return lo;
}
/** Кадр разбора, который сейчас на экране плеера. */
function shownIndex() {
    return frameIndex(S.shownT >= 0 ? S.shownT : el.video.currentTime);
}
/** Окно «крупно глаза» — по сглаженному центру глаз, чтобы увеличение не дрожало. */
function buildZoomTrack() {
    const an = S.an;
    const cx = new Float32Array(an.n), cy = new Float32Array(an.n), ew = new Float32Array(an.n), ok = new Float32Array(an.n);
    for (let i = 0; i < an.n; i++) {
        if (!an.has[i]) continue;
        const o = i * STRIDE;
        const r = eyeFrom(an.eyes, o), l = eyeFrom(an.eyes, o + EYE_FLOATS);
        cx[i] = (r.mid[0] + l.mid[0]) / 2; cy[i] = (r.mid[1] + l.mid[1]) / 2; ew[i] = Math.hypot(l.mid[0] - r.mid[0], l.mid[1] - r.mid[1]);
        ok[i] = 1;
    }
    const s = Math.max(2, (S.fps || 30) * 0.5);
    S.zoomC = { x: gaussSmooth(cx, ok, s), y: gaussSmooth(cy, ok, s), d: gaussSmooth(ew, ok, s * 2), ok };
}
function viewRect(i) {
    if (!S.zoom || !S.zoomC || i < 0 || !S.zoomC.ok.some((x) => x)) return { x: 0, y: 0, w: S.W, h: S.H };
    const d = Math.max(40, S.zoomC.d[i] || 0);
    // Оба глаза с бровями во всю ширину полосы; окно — в пропорциях холста.
    const ar = el.canvas.height / Math.max(1, el.canvas.width);
    let w = d * ZOOM_SPAN, h = w * ar;
    if (w > S.W) { w = S.W; h = w * ar; }
    if (h > S.H) { h = S.H; w = h / ar; }
    const x = clamp(S.zoomC.x[i] - w / 2, 0, S.W - w), y = clamp(S.zoomC.y[i] - h * 0.45, 0, S.H - h);
    return { x, y, w, h };
}
/** Поправка глаза e на кадре i по плану: [du, dv] в ширинах глаза или null. */
function shiftOf(i, e) {
    const P = S.P, k = P.w[e][i];
    if (!(k > 0.001)) return null;
    const du = P.du[e][i] * k, dv = P.dv[e][i] * k;
    return Math.hypot(du, dv) < 0.002 ? null : [du, dv];
}
/** Исправить глаза кадра i на холсте КАДРА (сохранение): пиксели — как есть, в родном размере. */
function correctFrame(ctx, i, sx, sy, ox, oy) {
    const P = S.P, an = S.an;
    if (!P || !an || i < 0 || i >= an.n || !an.has[i]) return 0;
    const W = ctx.canvas.width, H = ctx.canvas.height;
    let n = 0;
    for (let e = 0; e < 2; e++) {
        const d = shiftOf(i, e);
        if (!d) continue;
        const g = eyeFrom(an.eyes, i * STRIDE + e * EYE_FLOATS, sx, sy, ox, oy);
        if (g.w < 6) continue;
        const roi = eyeROI(g, W, H);
        if (roi.w < 4 || roi.h < 4) continue;
        const img = ctx.getImageData(roi.x, roi.y, roi.w, roi.h);
        const src = new Uint8ClampedArray(img.data);
        warpEye(src, img.data, roi, g, d[0], d[1], { openCap: P.openCap });
        ctx.putImageData(img, roi.x, roi.y);
        n++;
    }
    return n;
}
/**
 * Исправить глаза в ПРЕВЬЮ. Глаз перерисовывается на пикселях самого ролика —
 * тех же, что уйдут в файл, — и готовая заплатка кладётся на холст в его
 * масштабе. До 14.10.2026 превью перерисовывало уже уменьшенный кадр: у
 * вертикального 4K глаз на холсте — 35–45 px, радужка — 8 px, и человек
 * оценивал не то, что получит (обращение #1901). На паузе заплатка — в родном
 * размере ролика; на ходу — не шире PLAY_EYE_PX на глаз, чтобы успевать за
 * плеером (перерисовка от масштаба не зависит: все её допуски — в ширинах глаза).
 */
function correctPreview(ctx, v, i, r) {
    const P = S.P, an = S.an;
    if (!P || !an || i < 0 || i >= an.n || !an.has[i]) return 0;
    const c = ctx.canvas, kx = c.width / r.w, ky = c.height / r.h;
    const cap = S.playing ? PLAY_EYE_PX : PAUSE_EYE_PX;
    const patch = S.patch || (S.patch = document.createElement('canvas'));
    const pctx = patch.getContext('2d', { willReadFrequently: true });
    let n = 0;
    for (let e = 0; e < 2; e++) {
        const d = shiftOf(i, e);
        if (!d) continue;
        const off = i * STRIDE + e * EYE_FLOATS;
        const g0 = eyeFrom(an.eyes, off);
        if (g0.w < 6) continue;
        const roi = eyeROI(g0, S.W, S.H);
        if (roi.w < 4 || roi.h < 4) continue;
        // глаз за краем показанного окна — рисовать нечего
        if (roi.x + roi.w < r.x || roi.x > r.x + r.w || roi.y + roi.h < r.y || roi.y > r.y + r.h) continue;
        // не мельче, чем глаз выйдет на холсте, и не крупнее потолка
        const s = Math.min(1, Math.max(cap, g0.w * kx) / g0.w);
        const pw = Math.max(4, Math.round(roi.w * s)), ph = Math.max(4, Math.round(roi.h * s));
        if (patch.width !== pw || patch.height !== ph) { patch.width = pw; patch.height = ph; }
        pctx.imageSmoothingQuality = 'high';
        pctx.drawImage(v, roi.x, roi.y, roi.w, roi.h, 0, 0, pw, ph);
        const img = pctx.getImageData(0, 0, pw, ph);
        const src = new Uint8ClampedArray(img.data);
        const g = eyeFrom(an.eyes, off, pw / roi.w, ph / roi.h, roi.x, roi.y);
        warpEye(src, img.data, { x: 0, y: 0, w: pw, h: ph }, g, d[0], d[1], { openCap: P.openCap });
        pctx.putImageData(img, 0, 0);
        ctx.drawImage(patch, 0, 0, pw, ph, (roi.x - r.x) * kx, (roi.y - r.y) * ky, roi.w * kx, roi.h * ky);
        n++;
    }
    return n;
}
function drawFrame() {
    const v = el.video, c = el.canvas;
    if (!v.videoWidth || !c.width) return;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    const i = shownIndex();
    const r = viewRect(i);
    // Кадр 4K в окне шириной 300 px: без сглаживания «high» браузер берёт
    // четыре пикселя из шестидесяти, и щетина с ресницами рябят.
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(v, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
    let showFixed = S.view === 'fixed' && !S.holding && !S.previewLost;
    if (showFixed) {
        // Холст, «заражённый» потерянным файлом, отвечает SecurityError на
        // getImageData — один раз сказать и перестать, а не бросать с каждого кадра.
        try { correctPreview(ctx, v, i, r); }
        catch (e) {
            if (!isTaint(e)) throw e;
            previewLost('tainted');
            showFixed = false;
        }
    }
    el.tag.textContent = showFixed ? (I.view_fixed || '') : (I.view_orig || '');
    el.tag.classList.toggle('orig', !showFixed);
    S.lastIdx = i;
    el.timeCur.textContent = clock(v.currentTime);
    if (!S.seekDrag) el.seek.value = String(Math.round((v.currentTime / Math.max(0.001, S.dur)) * 1000));
    paintPlayhead();
}
/** Без requestVideoFrameCallback кадры на ходу рисует таймер экрана. */
function loopTick() {
    if (el.video.paused || !S.playing) { S.raf = 0; return; }
    drawFrame();
    S.raf = requestAnimationFrame(loopTick);
}
function play() {
    const v = el.video;
    if (!S.an || S.analyzing || S.exporting) return;
    v.muted = false;
    S.playing = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => { v.muted = true; v.play().catch(() => { S.playing = false; /* автоплей запрещён — остаёмся на паузе */ }); });
    el.play.setAttribute('aria-pressed', 'true');
    el.play.setAttribute('aria-label', I.pause_label || '');
    // С requestVideoFrameCallback кадр рисует watchFrames() — на каждый ПОКАЗАННЫЙ кадр.
    if (typeof v.requestVideoFrameCallback !== 'function') loopTick();
}
function stopPlayback() {
    const v = el.video;
    S.playing = false;
    if (v && !v.paused) v.pause();
    if (S.raf) cancelAnimationFrame(S.raf);
    S.raf = 0;
    if (el.play) { el.play.setAttribute('aria-pressed', 'false'); el.play.setAttribute('aria-label', I.play_label || ''); }
}
async function seekAndDraw(t) {
    await seekTo(t, 1500);
    drawFrame();
}

/* ───────────────────────────── график взгляда ──────────────────────────── */
const G_MAX = 30;   // градусов по высоте графика
function cssVar(n, fb) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(n).trim();
    return v || fb;
}
function paintGraph() {
    const c = el.graph, P = S.P, an = S.an;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = Math.max(100, Math.round(rect.width * dpr)), H = Math.max(40, Math.round(rect.height * dpr));
    if (c.width !== W || c.height !== H) { c.width = W; c.height = H; }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    if (!P || !an || !an.n) return;
    const n = an.n, x = (i) => (i / Math.max(1, n - 1)) * W, y = (deg) => H - (clamp(deg, 0, G_MAX) / G_MAX) * (H - 4 * dpr) - 2 * dpr;
    // полосы ворот
    const bands = { [GATE.CLOSED]: 'rgba(148,163,184,.22)', [GATE.LOOK_AWAY]: 'rgba(251,191,36,.20)', [GATE.NO_FACE]: 'rgba(100,116,139,.14)', [GATE.SMALL]: 'rgba(100,116,139,.14)', [GATE.TURNED]: 'rgba(251,191,36,.12)' };
    for (let i = 0; i < n; i++) {
        const g = P.gate[i];
        if (!g) continue;
        ctx.fillStyle = bands[g] || 'transparent';
        ctx.fillRect(Math.floor(x(i)), 0, Math.max(1, Math.ceil(W / n)), H);
    }
    // линия камеры
    ctx.strokeStyle = cssVar('--border', 'rgba(255,255,255,.15)');
    ctx.lineWidth = dpr;
    ctx.beginPath(); ctx.moveTo(0, y(0)); ctx.lineTo(W, y(0)); ctx.stroke();
    const line = (fa, color, width) => {
        ctx.strokeStyle = color; ctx.lineWidth = width * dpr; ctx.beginPath();
        let started = false;
        for (let i = 0; i < n; i++) {
            if (!an.has[i]) { started = false; continue; }
            const X = x(i), Y = y(fa(i));
            if (!started) { ctx.moveTo(X, Y); started = true; } else ctx.lineTo(X, Y);
        }
        ctx.stroke();
    };
    line((i) => Math.hypot(P.gv[i], P.gh[i]), cssVar('--text-muted', 'rgba(148,163,184,.8)'), 1.2);
    line((i) => Math.hypot(P.gvAfter[i], P.ghAfter[i]), cssVar('--accent', '#38bdf8'), 1.8);
    // Ось подписана: без неё «было/стало» — две кривые непонятно чего.
    // Низ графика — взгляд ровно в объектив, верх — 30° мимо него. Подписи
    // ПОВЕРХ линий и на подложке: «стало» ходит у нуля и перечёркивало текст.
    ctx.font = (12 * dpr) + 'px system-ui, sans-serif';
    const tag = (txt, top) => {
        const tw = ctx.measureText(txt).width, ph = 16 * dpr, px = 4 * dpr;
        const ty = top ? 2 * dpr : H - ph - 2 * dpr;
        ctx.fillStyle = cssVar('--bg-secondary', 'rgba(15,23,42,.85)');
        ctx.globalAlpha = 0.85;
        ctx.fillRect(2 * dpr, ty, tw + 2 * px, ph);
        ctx.globalAlpha = 1;
        ctx.fillStyle = cssVar('--text-secondary', 'rgba(203,213,225,.95)');
        ctx.textBaseline = 'middle';
        ctx.fillText(txt, 2 * dpr + px, ty + ph / 2);
    };
    tag(G_MAX + '°', true);
    tag((I.graph_camera || 'camera') + ' · 0°', false);
    if (S.params.anchor >= 0) {
        ctx.fillStyle = cssVar('--success', '#34d399');
        ctx.beginPath(); ctx.arc(x(S.params.anchor), y(0), 4 * dpr, 0, Math.PI * 2); ctx.fill();
    }
    paintPlayhead();
}
function paintPlayhead() {
    const h = el.graphHead;
    if (!h || !S.an || !S.an.n || S.lastIdx < 0) return;
    h.style.left = ((S.lastIdx / Math.max(1, S.an.n - 1)) * 100) + '%';
    h.hidden = false;
}

/* ───────────────────────────── сохранение ──────────────────────────────── */
function outName() {
    const base = (S.file && S.file.name ? S.file.name.replace(/\.[^.]+$/, '') : 'video') + '-eye-contact.mp4';
    try { return window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(base) : base; } catch (_) { return base; }
}
async function onExport() {
    if (!S.P || S.exporting || S.analyzing) return;
    stopPlayback();
    let run = null;
    if (window.__toolLimit && window.__toolLimit.begin) {
        run = window.__toolLimit.begin(SLUG, S.limitKey);
        if (!run) return;
    }
    S.exporting = true;
    S.abort = new AbortController();
    const ctrl = S.abort;
    lock(true);
    const t0 = performance.now();
    const prog = { done: 0, total: S.an.n };
    let lane = S.lane;
    stage('export_start', { lane, frames: S.an.n, sec: Math.round(S.dur) });
    progress(true, fmt(I.exporting, { done: 0, total: prog.total }), 0, '');
    try {
        let blob = null;
        if (lane === 'hw') {
            try { blob = await guarded((sig, beat, alive) => exportHw(prog, t0, sig, beat, alive), 'export', exportSilence(prog)); }
            catch (e) {
                if (isAbort(e, ctrl)) throw e;
                aeRaw('tool_diagnostic', { issue: 'ec_hw_export_failed', msg: String(e && e.message).slice(0, 140) });
                lane = 'seek'; prog.done = 0;
            }
        }
        if (!blob) {
            const video = await guarded((sig, beat, alive) => exportSeek(prog, t0, sig, beat, alive), 'export', exportSilence(prog));
            // Звук доклеивается ВНЕ сторожа тишины: FFmpeg-ядро (~30 МБ) на
            // медленной сети законно грузится минутами, а у его загрузчика
            // свой сторож (30 с без байт) — второй убил бы готовое видео.
            blob = await muxSourceAudio(video);
        }
        if (!blob || blob.size < 1000) throw new Error('empty-output');
        const ok = window.__downloadFile ? window.__downloadFile(blob, outName()) : false;
        if (ok !== false) {
            run && run.commit && run.commit();
            toast(I.saved || '', 'success');
            const st = S.P.stats;
            const cs = contactShares() || { before: 0, after: 0 };
            S.exportN = (S.exportN || 0) + 1;
            window.__aeToolComplete && window.__aeToolComplete({
                contactBefore: Math.round(cs.before * 100), contactAfter: Math.round(cs.after * 100), n: S.exportN,
                slug: SLUG, lane, frames: S.an.n, sec: Math.round(S.dur), w: S.W, h: S.H,
                sizeKB: Math.round(blob.size / 1024),
                corrected: Math.round(st.correctedShare * 100), meanDeg: Math.round(st.meanCorrectionDeg * 10) / 10,
                closed: Math.round(st.closedShare * 100), away: Math.round(st.lookAwayShare * 100),
                strength: S.params.strength, keep: S.params.keep, horiz: S.params.horizontal ? 1 : 0,
                aimV: S.params.aimV, aimH: S.params.aimH, anchor: S.params.anchor >= 0 ? 1 : 0,
                msPerFrame: prog.done ? Math.round((performance.now() - t0) / prog.done) : 0,
            });
        } else {
            run && run.abort && run.abort();
        }
    } catch (e) {
        run && run.abort && run.abort();
        const aborted = isAbort(e, ctrl);
        // Запасной лейн сохранения рисует кадры с <video>: файл, пропавший из-под
        // него, — потерянный доступ, а не «не получилось сохранить, попробуйте ещё».
        if (!aborted && (isTaint(e) || isTaint(e && e.inner))) {
            previewLost('tainted');
            toast(I.fail_lost || '', 'error');
            aeRaw('tool_error', { f: 'export_media_lost', lane });
            return;
        }
        const unsupported = /^unsupported:/.test(String(e && e.message));
        toast(aborted ? (I.cancelled || '') : ((unsupported && I.fail_no_encoder) || I.err_export || ''), aborted ? 'info' : 'error');
        if (!aborted) {
            window.__handleError && window.__handleError(e, SLUG);
            aeRaw('tool_error', { f: e && e.ecStall ? 'export_stall' : 'export_failed', lane, msg: String(e && e.message).slice(0, 140) });
        }
    } finally {
        S.exporting = false; S.abort = null;
        progress(false);
        lock(false);
        drawFrame();
    }
}
/** Допустимая тишина сохранения: кадры идут — минута; кадры кончились — сборка звука. */
function exportSilence(prog) {
    return () => (prog.done >= prog.total - 1
        ? Math.max(stallMs(), STALL_FINAL_MS, S.dur * 250)
        : stallMs());
}
function progressExport(prog, t0) {
    const f = clamp(prog.done / Math.max(1, prog.total), 0, 1);
    const el_ = (performance.now() - t0) / 1000;
    const eta = f > 0.03 ? Math.max(0, el_ / f - el_) : 0;
    progress(true, fmt(I.exporting, { done: prog.done, total: prog.total }), f, eta ? fmt(I.eta, { t: human(eta) }) : '');
}
async function exportHw(prog, t0, sig, beat, alive) {
    const P = window.WebCodecsPipeline;
    const W = S.W, H = S.H;
    let idx2 = 0, innerErr = null, timeMiss = 0;
    const res = await P.run({
        slug: SLUG, file: S.file, params: {}, crf: 20,
        geometry2d: {
            width: W - (W & 1), height: H - (H & 1),
            draw(gctx, drawVideo, cw, ch, idx, t) {
                drawVideo(0, 0, W, H, 0, 0, cw, ch);
                if (innerErr || !alive()) return;
                beat();
                // Кадры обоих проходов идут в одном порядке; сверка по времени
                // ловит расхождение, если декодер выдал их иначе.
                let i = idx2++;
                if (i >= S.an.n || Math.abs(S.an.t[i] - t) > 0.75 / S.fps) { i = frameIndex(t); timeMiss++; }
                try { correctFrame(gctx, i, cw / W, ch / H, 0, 0); }
                catch (e) { innerErr = e; return; }
                prog.done++;
                if (prog.done % 10 === 1) progressExport(prog, t0);
            },
        },
        onProgress() { /* считаем по кадрам */ },
        abortSignal: sig,
    });
    if (innerErr) throw innerErr;
    if (timeMiss) aeRaw('tool_diagnostic', { issue: 'ec_time_miss', n: timeMiss });
    if (!res || !res.blob || res.blob.size < 1000) throw new Error('hw-empty-output');
    if (res.audioExpected > 0 && res.audioMuxed === 0) throw new Error('hw-audio-dropped');
    return res.blob;
}
async function exportSeek(prog, t0, sig, beat, alive) {
    const CVE = window.CanvasVideoExport;
    if (!CVE) throw new Error('exporter-missing');
    const W = S.W - (S.W & 1), H = S.H - (S.H & 1);
    const sup = await CVE.isSupported({ width: W, height: H, fps: S.fps, format: 'mp4' });
    if (!sup || !sup.ok) throw new Error('unsupported:' + (sup && sup.reason));
    const work = document.createElement('canvas');
    work.width = W; work.height = H;
    const wctx = work.getContext('2d', { willReadFrequently: true });
    prog.total = Math.max(1, Math.floor(S.dur * S.fps));
    const video = await CVE.encode({
        width: W, height: H, fps: S.fps, durationSec: prog.total / S.fps,
        format: 'mp4', qualityCrf: 20, signal: sig,
        drawFrame: async (ctx, idx) => {
            if (!alive()) return;
            beat();
            const t = (idx + 0.5) / S.fps;
            await seekTo(t);
            wctx.drawImage(el.video, 0, 0, W, H);
            correctFrame(wctx, frameIndex(t), W / S.W, H / S.H, 0, 0);
            ctx.drawImage(work, 0, 0);
            prog.done++;
            if (prog.done % 5 === 1) progressExport(prog, t0);
        },
        onProgress() { /* считаем по кадрам */ },
    });
    if (!video) throw new DOMException('cancelled', 'AbortError');
    return video;
}
/** Звук исходника в видео запасного лейна (FFmpeg: копия картинки, AAC звука). */
async function muxSourceAudio(video) {
    const CVE = window.CanvasVideoExport;
    const sig = S.abort ? S.abort.signal : undefined;
    // Ролик без звука: доклейка упала бы на «-map 1:a:0», и человек прочёл бы
    // ложное «звук перенести не удалось». Ответ — у контейнера (общий примитив).
    try {
        if (typeof window.__detectVideoAudio === 'function') {
            const r = await Promise.race([
                window.__detectVideoAudio(S.file, { videoEl: el.video }),
                new Promise((res) => setTimeout(() => res(null), 8000)),
            ]);
            if (r && r.confident && r.hasAudio === false) return video;
        }
    } catch (_) { /* не знаем — пробуем доклеить, как раньше */ }
    let muxed = null;
    try { muxed = CVE && CVE.muxAudioInto ? await CVE.muxAudioInto(video, S.file, { format: 'mp4', signal: sig }) : null; }
    catch (_) { muxed = null; }
    if (sig && sig.aborted) throw new DOMException('cancelled', 'AbortError');
    if (!muxed) { toast(I.audio_dropped || '', 'warning'); ae('ec_audio_dropped'); }
    return muxed || video;
}

/* ───────────────────────────── органы ──────────────────────────────────── */
let replanTimer = 0;
function scheduleReplan() {
    clearTimeout(replanTimer);
    replanTimer = setTimeout(() => { replan(); drawFrame(); }, 30);
}
function bindRange(input, out, key, toParam, toText) {
    const apply = (save) => {
        S.params[key] = toParam(+input.value);
        out.textContent = toText(+input.value);
        if (save) savePref(key, S.params[key]);
        scheduleReplan();
    };
    input.addEventListener('input', () => apply(false));
    input.addEventListener('change', () => { apply(true); ae('ec_' + key, { v: +input.value }); });
}
function syncControls() {
    el.strength.value = String(Math.round(S.params.strength * 100));
    el.strengthOut.textContent = fmt(I.pct, { n: el.strength.value });
    el.keep.value = String(Math.round(S.params.keep * 100));
    el.keepOut.textContent = fmt(I.pct, { n: el.keep.value });
    el.horiz.checked = !!S.params.horizontal;
    el.aimV.value = String(-S.params.aimV); el.aimVOut.textContent = fmt(I.deg, { n: -S.params.aimV });
    el.aimH.value = String(S.params.aimH); el.aimHOut.textContent = fmt(I.deg, { n: S.params.aimH });
    el.away.value = String(S.params.lookAway); el.awayOut.textContent = fmt(I.deg, { n: S.params.lookAway });
    paintCalib();
}
function paintCalib() {
    const on = S.params.anchor >= 0 && S.an;
    el.calibLine.hidden = !on;
    if (on) el.calibTxt.textContent = fmt(I.calib_on, { t: clock(S.an.t[S.params.anchor]) });
}
function bindControls() {
    bindRange(el.strength, el.strengthOut, 'strength', (v) => v / 100, (v) => fmt(I.pct, { n: v }));
    bindRange(el.keep, el.keepOut, 'keep', (v) => v / 100, (v) => fmt(I.pct, { n: v }));
    // Ползунок «ниже ↔ выше»: вправо — ВЫШЕ, а в модели + это ниже.
    bindRange(el.aimV, el.aimVOut, 'aimV', (v) => -v, (v) => fmt(I.deg, { n: v }));
    bindRange(el.aimH, el.aimHOut, 'aimH', (v) => v, (v) => fmt(I.deg, { n: v }));
    bindRange(el.away, el.awayOut, 'lookAway', (v) => v, (v) => fmt(I.deg, { n: v }));
    el.horiz.addEventListener('change', () => {
        S.params.horizontal = el.horiz.checked; savePref('horizontal', S.params.horizontal);
        ae('ec_horizontal', { on: el.horiz.checked ? 1 : 0 });
        scheduleReplan();
    });
    el.calib.addEventListener('click', () => {
        if (!S.an || !S.P) return;
        const i = shownIndex();
        const good = i >= 0 && S.an.has[i] && S.P.gate[i] !== GATE.CLOSED && S.P.gate[i] !== GATE.NO_FACE && S.P.gate[i] !== GATE.SMALL;
        if (!good) { toast(I.calib_no_eyes || '', 'warning'); return; }
        S.params.anchor = i;
        ae('ec_calibrate', { t: Math.round(S.an.t[i] * 10) / 10 });
        paintCalib(); replan(); drawFrame();
    });
    el.calibReset.addEventListener('click', () => { S.params.anchor = -1; paintCalib(); replan(); drawFrame(); });
    el.reset.addEventListener('click', () => {
        Object.assign(S.params, { strength: 1, keep: 0.75, horizontal: true, aimV: 0, aimH: 0, lookAway: 24, anchor: -1 });
        for (const k of PREF) savePref(k, S.params[k]);
        syncControls(); replan(); drawFrame();
        ae('ec_reset');
    });
    // Просмотр
    el.play.addEventListener('click', () => { if (el.video.paused) play(); else { stopPlayback(); drawFrame(); } });
    el.seek.addEventListener('input', () => {
        S.seekDrag = true;
        const t = (+el.seek.value / 1000) * S.dur;
        el.timeCur.textContent = clock(t);
        if (!el.video.paused) stopPlayback();
        seekAndDraw(t);
    });
    el.seek.addEventListener('change', () => { S.seekDrag = false; });
    for (const b of el.viewSeg.querySelectorAll('[data-view]')) {
        b.addEventListener('click', () => {
            S.view = b.getAttribute('data-view');
            for (const x of el.viewSeg.querySelectorAll('[data-view]')) {
                const on = x === b;
                x.classList.toggle('on', on); x.setAttribute('aria-selected', on ? 'true' : 'false');
            }
            ae('ec_view', { v: S.view });
            drawFrame();
        });
    }
    el.zoom.addEventListener('click', () => {
        S.zoom = !S.zoom;
        el.zoom.setAttribute('aria-pressed', S.zoom ? 'true' : 'false');
        ae('ec_zoom', { on: S.zoom ? 1 : 0 });
        if (S.W) sizeCanvas();
        drawFrame();
    });
    // Удержание кадра — оригинал (мышь, палец, пробел не трогаем: он у плеера).
    const hold = (on) => {
        if (S.holding === on) return;
        S.holding = on; drawFrame();
        if (on && !S.heldOnce) { S.heldOnce = true; ae('ec_hold'); }
    };
    // Мышь — оригинал сразу. Палец — после долгого нажатия: кадр отдаёт
    // вертикальный свайп прокрутке (touch-action: pan-y), и мгновенный показ
    // оригинала мигал бы на каждой прокрутке. До 25.09 здесь стояло
    // touch-action:none — на iPhone палец, лёгший на видео (полэкрана),
    // страницу не прокручивал вовсе (эмуляция iPhone 15, CDP-свайп).
    let holdTimer = 0, holdAt = null;
    el.canvas.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        if (e.pointerType === 'mouse') { hold(true); try { el.canvas.setPointerCapture(e.pointerId); } catch (_) { /* не критично */ } return; }
        holdAt = { x: e.clientX, y: e.clientY, id: e.pointerId };
        clearTimeout(holdTimer);
        holdTimer = setTimeout(() => { if (holdAt) hold(true); }, HOLD_DELAY_MS);
    });
    el.canvas.addEventListener('pointermove', (e) => {
        if (!holdAt || e.pointerId !== holdAt.id || S.holding) return;
        if (Math.hypot(e.clientX - holdAt.x, e.clientY - holdAt.y) > HOLD_SLOP_PX) { clearTimeout(holdTimer); holdAt = null; }
    });
    const release = () => { clearTimeout(holdTimer); holdAt = null; hold(false); };
    el.canvas.addEventListener('pointerup', release);
    el.canvas.addEventListener('pointercancel', release);
    el.canvas.addEventListener('lostpointercapture', release);
    el.canvas.addEventListener('contextmenu', (e) => { if (S.holding || holdAt) e.preventDefault(); });
    // График: нажатие — переход к моменту
    const graphSeek = (e) => {
        if (!S.an) return;
        const r = el.graph.getBoundingClientRect();
        const f = clamp((e.clientX - r.left) / Math.max(1, r.width), 0, 1);
        const i = Math.round(f * (S.an.n - 1));
        if (!S.graphOnce) { S.graphOnce = true; ae('ec_graph_seek'); }
        if (!el.video.paused) stopPlayback();
        // в середину кадра: на его границе плеер вправе показать предыдущий
        seekAndDraw(i < S.an.n ? S.an.t[i] + 0.5 / (S.fps || 30) : f * S.dur);
    };
    // График: мышь — нажал и тяни; палец — тап или ГОРИЗОНТАЛЬНОЕ ведение,
    // вертикаль остаётся прокрутке страницы (touch-action: pan-y).
    let gAt = null;
    el.graph.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') { graphSeek(e); try { el.graph.setPointerCapture(e.pointerId); } catch (_) { /* не критично */ } return; }
        gAt = { x: e.clientX, id: e.pointerId };
    });
    el.graph.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse') { if (e.buttons & 1) graphSeek(e); return; }
        if (gAt && e.pointerId === gAt.id && Math.abs(e.clientX - gAt.x) > 6) graphSeek(e);
    });
    el.graph.addEventListener('pointerup', (e) => { if (e.pointerType !== 'mouse' && gAt) graphSeek(e); gAt = null; });
    el.graph.addEventListener('pointercancel', () => { gAt = null; });
    el.exportBtn.addEventListener('click', onExport);
    el.cancel.addEventListener('click', () => { ae('ec_cancel', { phase: 'export' }); S.abort && S.abort.abort(); });
    el.veilStop.addEventListener('click', () => { ae('ec_cancel', { phase: 'analyze' }); S.abort && S.abort.abort(); });
    el.video.addEventListener('ended', () => { stopPlayback(); drawFrame(); });
    let rt = 0;
    window.addEventListener('resize', () => {
        clearTimeout(rt);
        rt = setTimeout(() => { if (!S.W) return; sizeCanvas(); drawFrame(); paintGraph(); }, 120);
    });
}

function reset() {
    stopPlayback();
    if (S.abort) { try { S.abort.abort(); } catch (_) { /* уже */ } }
    S.file = null; S.an = null; S.M = null; S.P = null;
    showFail(false);
    el.work.hidden = true;
    veil(false); progress(false);
}

function init() {
    const ids = {
        zone: 'ecUpload', fail: 'ecFail', work: 'ecWork', rail: 'ecRail', view: 'ecView', canvas: 'ecCanvas', tag: 'ecTag',
        veil: 'ecVeil', veilTxt: 'ecVeilTxt', veilFill: 'ecVeilFill', veilSub: 'ecVeilSub', veilStop: 'ecVeilStop',
        play: 'ecPlay', seek: 'ecSeek', timeCur: 'ecTimeCur', timeDur: 'ecTimeDur', viewSeg: 'ecViewSeg', zoom: 'ecZoom',
        graph: 'ecGraph', graphHead: 'ecGraphHead', stats: 'ecStats',
        strength: 'ecStrength', strengthOut: 'ecStrengthOut', keep: 'ecKeep', keepOut: 'ecKeepOut', horiz: 'ecHoriz',
        aimV: 'ecAimV', aimVOut: 'ecAimVOut', aimH: 'ecAimH', aimHOut: 'ecAimHOut', away: 'ecAway', awayOut: 'ecAwayOut',
        calib: 'ecCalib', calibLine: 'ecCalibLine', calibTxt: 'ecCalibTxt', calibReset: 'ecCalibReset', reset: 'ecReset',
        exportBtn: 'ecExport', prog: 'ecProg', progTxt: 'ecProgTxt', progFill: 'ecProgFill', progEta: 'ecProgEta', cancel: 'ecCancel',
        video: 'ecVideo',
    };
    for (const k in ids) el[k] = $(ids[k]);
    if (!el.work || !el.zone) return;
    if (noEncoder()) {
        showFail('no_encoder');
        aeRaw('tool_blocked', { f: 'no_export_support', why: 'no-webcodecs', at: 'page' });
    }
    loadPrefs();
    syncControls();
    bindControls();
    // Через __bindUploaderFile, а не голым addEventListener: файл мог быть
    // выбран раньше, чем модуль подписался (сторож UploaderListenerRaceTest).
    const onFile = (file, detail) => { const f = file || (detail && detail.files && detail.files[0]); if (f) load(f); };
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(el.zone, onFile);
    else el.zone.addEventListener('tool-uploader:file', (e) => onFile(e.detail && e.detail.file, e.detail));
    document.addEventListener('tool-uploader:clear', reset);
    // Модель лица — заранее, по первому признаку намерения (наведение на зону).
    const warm = () => { try { window.FRLandmarks && window.FRLandmarks.warm(CFG.sources || [], { numFaces: 2, mode: 'IMAGE', blendshapes: true, matrix: true }); } catch (_) { /* прогрев — не обязанность */ } };
    el.zone.addEventListener('pointerenter', warm, { once: true });
    el.zone.addEventListener('focusin', warm, { once: true });
    window.__ecDebug = { S, replan, drawFrame, frameIndex, shownIndex };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
